import React, { useEffect, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { useMaintenanceMode } from "../context/MaintenanceContext";
import {
  Skeleton,
  Card,
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  useTheme,
} from "@mui/material";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import WidgetSelection from "./WidgetSelection";
import Widget from "./Widget";
import { useAuth } from "../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import Layout from "./Layout";
import MaintenancePopup from "./MaintenancePopup";

const ResponsiveGridLayout = WidthProvider(Responsive);

// Register Chart.js components globally
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend
);
// Cache configuration - NO EXPIRATION, persistent cache
const CACHE_KEY_PREFIX = 'dashboard_severity_';

// Cache utility functions
const getCacheKey = (granularity, cumulation) => {
  return `${CACHE_KEY_PREFIX}${granularity}_${cumulation}`;
};

const getCachedData = (granularity, cumulation) => {
  try {
    const cacheKey = getCacheKey(granularity, cumulation);
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
      const { data } = JSON.parse(cached);
      return data;
    }
  } catch (error) {
    console.error('Error reading from cache:', error);
  }
  return null;
};

const setCachedData = (granularity, cumulation, data) => {
  try {
    const cacheKey = getCacheKey(granularity, cumulation);
    const cacheData = {
      data,
      timestamp: Date.now() // Keep timestamp for reference
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error writing to cache:', error);
  }
};
// ============== CACHE UTILITY FUNCTIONS ==============
const CACHE_KEYS = {
  PROJECT_STATUS: 'dashboard_project_status',
};

// Set cache without TTL - cache forever until data changes
const setCache = (key, data) => {
  const cacheItem = {
    data,
    timestamp: Date.now(), // Store timestamp for reference
  };
  localStorage.setItem(key, JSON.stringify(cacheItem));
};

// Get cache - no expiration check, returns data if exists
const getCache = (key) => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const cacheItem = JSON.parse(cached);
    return cacheItem.data; // Return cached data without expiration check
  } catch (error) {
    console.error('Cache retrieval error:', error);
    return null;
  }
};

// Clear specific cache
const clearCache = (key) => {
  localStorage.removeItem(key);
};

// Check if cached data is different from new data
const isCacheStale = (key, newData) => {
  const cached = getCache(key);
  if (!cached) return true; // No cache, consider stale
  
  // Compare stringified versions to check if data changed
  return JSON.stringify(cached) !== JSON.stringify(newData);
};
// =====================================================

// Widget data
const availableWidgets = [
  { name: "projectSummary", label: "Project Summary" },
  { name: "issuesSeverity", label: "Issues by Severity" },
  { name: "projectStatus", label: "Project Status Distribution" },
];

// Default grid layout for widgets
const defaultLayouts = {
  lg: [
    { i: "projectSummary", x: 0, y: 0, w: 12, h: 2 },
    { i: "issuesSeverity", x: 0, y: 2, w: 6, h: 4 },
    { i: "projectStatus", x: 6, y: 2, w: 6, h: 4 },
  ],
  md: [
    { i: "projectSummary", x: 0, y: 0, w: 10, h: 2 },
    { i: "issuesSeverity", x: 0, y: 2, w: 5, h: 4 },
    { i: "projectStatus", x: 5, y: 2, w: 5, h: 4 },
  ],
  sm: [
    { i: "projectSummary", x: 0, y: 0, w: 12, h: 2 },
    { i: "issuesSeverity", x: 0, y: 4, w: 6, h: 4 },
    { i: "projectStatus", x: 0, y: 6, w: 6, h: 3 },
  ],
  xs: [
    { i: "projectSummary", x: 0, y: 0, w: 8, h: 2 },
    { i: "issuesSeverity", x: 0, y: 4, w: 4, h: 4 },
    { i: "projectStatus", x: 0, y: 6, w: 4, h: 2 },
  ],
  xxs: [
    { i: "projectSummary", x: 0, y: 0, w: 4, h: 2 },
    { i: "issuesSeverity", x: 0, y: 4, w: 2, h: 4 },
    { i: "projectStatus", x: 0, y: 6, w: 2, h: 2 },
  ],
};

const Dashboard = () => {
  const { authToken, userRole } = useAuth();
  const token = authToken;
  const theme = useTheme();
  const navigate = useNavigate();

  const { isPageInMaintenance } = useMaintenanceMode();
  
  const [selectedWidgets, setSelectedWidgets] = useState(() => {
    const storedSelections = localStorage.getItem("selectedWidgets");
    return storedSelections ? JSON.parse(storedSelections) : {};
  });
  
  const [layouts, setLayouts] = useState(() => {
    const storedLayouts = localStorage.getItem("layouts");
    return storedLayouts ? JSON.parse(storedLayouts) : { ...defaultLayouts };
  });
  
  const [loadingSeverity, setLoadingSeverity] = useState(true);
  const [loadingProjectStatus, setLoadingProjectStatus] = useState(true); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [chartData, setChartData] = useState({
    totalProjects: 0,
    completedProjects: 0,
    ongoingProjects: 0,
    pendingProjects: 0,
    codePushed: 0,
    codeLimit: 0,
    severityCounts: { info: 0, minor: 0, major: 0, critical: 0, blocker: 0 },
  });
  const [apiData, setApiData] = useState(null);
  const [severityData, setSeverityData] = useState(null);
  const [planName, setPlanName] = useState(null);
  const [selectedGranularity, setSelectedGranularity] = useState("day");
  const [selectedCumulation, setSelectedCumulation] = useState("sum");
  const [organization, setOrganization] = useState("");
  const [projectStatusData, setProjectStatusData] = useState({
    totalProjects: 0,
    completed: 0,
    failed: 0,
    inProgress: 0,
  });

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setOrganization(data.organization.name);
        setPlanName(data.organization.plan_name);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
      });
  }, []);

  // Ensure layouts are validated on load
  useEffect(() => {
    const storedLayouts = localStorage.getItem("layouts");
    if (storedLayouts) {
      try {
        const parsedLayouts = JSON.parse(storedLayouts);
        const updatedLayouts = { ...defaultLayouts };
        
        Object.keys(defaultLayouts).forEach((breakpoint) => {
          if (parsedLayouts[breakpoint]) {
            updatedLayouts[breakpoint] = [...parsedLayouts[breakpoint]];
            defaultLayouts[breakpoint].forEach((defaultWidget) => {
              if (
                !parsedLayouts[breakpoint].some((w) => w.i === defaultWidget.i)
              ) {
                updatedLayouts[breakpoint].push(defaultWidget);
              }
            });
          }
        });

        setLayouts(updatedLayouts);
      } catch {
        setLayouts({ ...defaultLayouts });
      }
    } else {
      setLayouts({ ...defaultLayouts });
    }
  }, []);

  const handleWidgetSelectionChange = (newSelection) => {
    const updatedLayouts = { ...layouts };

    Object.keys(defaultLayouts).forEach((breakpoint) => {
      if (!updatedLayouts[breakpoint]) {
        updatedLayouts[breakpoint] = [];
      }

      Object.keys(newSelection).forEach((widgetKey) => {
        if (newSelection[widgetKey]) {
          if (!updatedLayouts[breakpoint].some((w) => w.i === widgetKey)) {
            const defaultWidgetLayout = defaultLayouts[breakpoint].find(
              (w) => w.i === widgetKey
            );
            if (defaultWidgetLayout) {
              const { x, y } = findNextAvailablePosition(
                updatedLayouts[breakpoint],
                defaultWidgetLayout.w,
                defaultWidgetLayout.h
              );
              updatedLayouts[breakpoint].push({
                ...defaultWidgetLayout,
                x,
                y,
              });
            }
          }
        } else {
          updatedLayouts[breakpoint] = updatedLayouts[breakpoint].filter(
            (w) => w.i !== widgetKey
          );
        }
      });

      updatedLayouts[breakpoint] = reflowWidgets(updatedLayouts[breakpoint]);
    });

    setLayouts(updatedLayouts);
    setSelectedWidgets(newSelection);
    localStorage.setItem("selectedWidgets", JSON.stringify(newSelection));
    localStorage.setItem("layouts", JSON.stringify(updatedLayouts));
  };

  const findNextAvailablePosition = (layout, w, h) => {
    const GRID_COLUMNS = 12;
    const occupiedSpaces = {};

    layout.forEach((widget) => {
      for (let x = widget.x; x < widget.x + widget.w; x++) {
        for (let y = widget.y; y < widget.y + widget.h; y++) {
          occupiedSpaces[`${x},${y}`] = true;
        }
      }
    });

    for (let y = 0; y < Infinity; y++) {
      for (let x = 0; x <= GRID_COLUMNS - w; x++) {
        let fits = true;
        for (let dx = 0; dx < w; dx++) {
          for (let dy = 0; dy < h; dy++) {
            if (occupiedSpaces[`${x + dx},${y + dy}`]) {
              fits = false;
              break;
            }
          }
          if (!fits) break;
        }
        if (fits) return { x, y };
      }
    }
    return { x: 0, y: 0 };
  };

  const reflowWidgets = (layout) => {
    const sortedLayout = [...layout].sort((a, b) =>
      a.y === b.y ? a.x - b.x : a.y - b.y
    );
    const GRID_COLUMNS = 12;
    const reflowedLayout = [];

    sortedLayout.forEach((widget) => {
      const { x, y } = findNextAvailablePosition(
        reflowedLayout,
        widget.w,
        widget.h
      );
      reflowedLayout.push({ ...widget, x, y });
    });

    return reflowedLayout;
  };

  const handleLayoutChange = (currentLayout, allLayouts) => {
    const updatedLayouts = { ...allLayouts };
    setLayouts(updatedLayouts);
    localStorage.setItem("layouts", JSON.stringify(updatedLayouts));
  };

  const reorderWidgets = () => {
    setLayouts(defaultLayouts);
    localStorage.setItem("layouts", JSON.stringify(defaultLayouts));
  };

  const processSeverityData = (data) => {
    if (!data || !data.issue_details_list) return null;

    const selectedvalue =
      selectedGranularity === "day"
        ? 7
        : selectedGranularity === "month"
        ? 6
        : 5;

    let processedData = data.issue_details_list.slice(0, selectedvalue);

    processedData = processedData.sort((a, b) => {
      const formatDate = (dateString, selected) => {
        if (selected === "day") {
          const [day, month, year] = dateString.split("-").map(Number);
          return new Date(year, month - 1, day);
        } else if (selected === "month") {
          const [month, year] = dateString.split("-").map(Number);
          return new Date(year, month - 1);
        }
        return null;
      };

      const dateA = formatDate(a[selectedGranularity], selectedGranularity);
      const dateB = formatDate(b[selectedGranularity], selectedGranularity);

      return dateA - dateB;
    });

    const labels = processedData.map((item) => item[selectedGranularity]);
    const infoSeverityData = processedData.map(
      (item) => item.severity_details.info || 0
    );
    const minorSeverityData = processedData.map(
      (item) => item.severity_details.minor || 0
    );
    const majorSeverityData = processedData.map(
      (item) => item.severity_details.major || 0
    );
    const criticalSeverityData = processedData.map(
      (item) => item.severity_details.critical || 0
    );
    const blockerSeverityData = processedData.map(
      (item) => item.severity_details.blocker || 0
    );

    return {
      labels,
      datasets: [
        {
          label: "Info Severity",
          data: infoSeverityData,
          backgroundColor: "#17A2B8",
        },
        {
          label: "Minor Severity",
          data: minorSeverityData,
          backgroundColor: "#6C757D",
        },
        {
          label: "Major Severity",
          data: majorSeverityData,
          backgroundColor: "#FFC107",
        },
        {
          label: "Critical Severity",
          data: criticalSeverityData,
          backgroundColor: "#DC3545",
        },
        {
          label: "Blocker Severity",
          data: blockerSeverityData,
          backgroundColor: "#343A40",
        },
      ],
    };
  };

  useEffect(() => {
    fetchAndProcessData();
  }, [selectedGranularity, selectedCumulation]);

  const fetchAndProcessData = async () => {
    const cachedData = getCachedData(selectedGranularity, selectedCumulation);
  
  if (cachedData) {
    console.log('✅ Loading severity data from cache - INSTANT');
    setApiData(cachedData);
    const processedSeverityData = processSeverityData(cachedData);
    setSeverityData(processedSeverityData);
    setLoadingSeverity(false);
    
    // Fetch fresh data in background (optional - for updates)
    fetchFreshDataInBackground();
    return;
  }

    setLoadingSeverity(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/dashboard-issues-severity-widget?granularity=${selectedGranularity}&cumulation=${selectedCumulation}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }
      const data = await response.json();
      setCachedData(selectedGranularity, selectedCumulation, data);
      setApiData(data);

      const processedSeverityData = processSeverityData(data);
      setSeverityData(processedSeverityData);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message);
    } finally {
      setLoadingSeverity(false);
    }
  };

  const fetchFreshDataInBackground = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/dashboard-issues-severity-widget?granularity=${selectedGranularity}&cumulation=${selectedCumulation}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (response.ok) {
      const data = await response.json();
      // Update cache silently
      setCachedData(selectedGranularity, selectedCumulation, data);
      console.log('🔄 Cache updated in background');
    }
  } catch (error) {
    console.log('Background update failed (not critical):', error);
  }
};

  const fetchProjectStatusData = async () => {
    setLoadingProjectStatus(true);
    setError(null);
    
    // Check cache first
    const cachedData = getCache(CACHE_KEYS.PROJECT_STATUS);
    if (cachedData) {
      console.log('Using cached project status data:', cachedData);
      setProjectStatusData(cachedData);
      setLoadingProjectStatus(false);
      
      // Fetch fresh data in background to check if it changed
      fetchAndUpdateProjectStatusCache();
      return;
    }

    // No cache, fetch from API
    await fetchAndUpdateProjectStatusCache();
  };

  const fetchAndUpdateProjectStatusCache = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/dashboard-project-status-widget`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch project status data");
      }

      const data = await response.json();
      
      const statusData = {
        totalProjects: data.total_projects || 0,
        completed: data.completed || 0,
        failed: data.failed || 0,
        inProgress: data["in-progress"] || 0,
      };
      
      // Check if data has changed
      if (isCacheStale(CACHE_KEYS.PROJECT_STATUS, statusData)) {
        console.log('Data changed, updating cache and UI:', statusData);
        setProjectStatusData(statusData);
        setCache(CACHE_KEYS.PROJECT_STATUS, statusData);
      } else {
        console.log('Data unchanged, keeping cache');
      }
      
    } catch (error) {
      console.error("Error fetching project status data:", error);
      setError(error.message);
    } finally {
      setLoadingProjectStatus(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProjectStatusData();
    }
  }, [token]);

  const pieChartData = {
    labels: ["Completed", "Failed", "In Progress"],
    datasets: [
      {
        data: [
          projectStatusData.completed,
          projectStatusData.failed,
          projectStatusData.inProgress,
        ],
        backgroundColor: ["#339e25", "#e73535", "#f7903c"],
        hoverBackgroundColor: ["#44c433", "#db4b4b", "#f0c23b"],
      },
    ],
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/plan/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch plans");
        return res.json();
      })
      .then((data) => {
        setPlans(data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingPlans(false));
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;

    return (
    <Layout>
      <Box sx={{ pposition: 'relative',
                width: '100%',
                overflowX: 'hidden',
                maxWidth: '100vw' 
              }}>
        <Box
          sx={{
            filter: isPageInMaintenance('dashboard') ? 'blur(4px)' : 'none',
            transition: 'filter 0.3s ease',
            pointerEvents: isPageInMaintenance('dashboard') ? 'none' : 'auto',
          }}
        >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "5px",
            p: 1,
          }}
        >
          <Box sx={{ ml: 1 }}>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Dashboard
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              {organization && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {organization}
                </Typography>
              )}

              {organization && planName && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Plan: {planName}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            {userRole === "superadmin" && (
              <Button
                variant="contained"
                className="athrv-dash-primary-button"
                onClick={() => navigate("/admindashboard")}
                sx={{ whiteSpace: "nowrap" }}
              >
                ADMIN DASHBOARD
              </Button>
            )}
            <Button
              variant="contained"
              className="athrv-dash-primary-button"
              onClick={reorderWidgets}
              sx={{ whiteSpace: "nowrap" }}
            >
              Reorder Widgets
            </Button>
            <WidgetSelection
              availableWidgets={availableWidgets}
              onWidgetSelectionChange={handleWidgetSelectionChange}
            />
          </Box>
        </Box>

        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={100}
          isResizable={true}
          isDraggable={true}
          onLayoutChange={handleLayoutChange}
          style={{ width: '100%' }}
        >
          {selectedWidgets.projectSummary && (
            <div key="projectSummary">
              <Widget title="Project Summary">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  {loadingProjectStatus ? (
                    [...Array(4)].map((_, index) => (
                      <Card
                        key={index}
                        elevation={2}
                        sx={{
                          p: 2,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          borderRadius: 2,
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={100}
                          sx={{
                            borderRadius: 2.5,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "#333"
                                : "rgba(0, 0, 0, 0.11)",
                          }}
                        />
                      </Card>
                    ))
                  ) : (
                    <>
                      {/* Total Projects Card */}
                      <Card
                        elevation={2}
                        sx={{
                          p: 0,
                          flex: 1,
                          display: "flex",
                          flexDirection: "row",
                          borderRadius: 2,
                          backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
                          overflow: 'hidden',
                        }}
                      >
                        {/* Left Gap/Spacing */}
                        <Box sx={{ width: '12px' }} />
                        
                        {/* Blue Vertical Line */}
                        <Box
                          sx={{
                            width: '4px',
                            backgroundColor: '#00CCFF',
                            borderRadius: '8px',
                            flexShrink: 0,
                            my: 1,
                          }}
                        />
                        
                        {/* Content */}
                        <Box sx={{ 
                          p: 2, 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flex: 1,
                        }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000", 
                              fontWeight: "bold", 
                              mb: 1 
                            }}
                          >
                            Total Projects:
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{ 
                              fontWeight: "bold", 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000" 
                            }}
                          >
                            {projectStatusData.totalProjects}
                          </Typography>
                        </Box>
                      </Card>

                      {/* Completed Projects Card */}
                      <Card
                        elevation={2}
                        sx={{
                          p: 0,
                          flex: 1,
                          display: "flex",
                          flexDirection: "row",
                          borderRadius: 2,
                          backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
                          overflow: 'hidden',
                        }}
                      >
                        {/* Left Gap/Spacing */}
                        <Box sx={{ width: '12px' }} />
                        
                        {/* Blue Vertical Line */}
                        <Box
                          sx={{
                            width: '4px',
                            backgroundColor: '#339e25',
                            borderRadius: '8px',
                            flexShrink: 0,
                            my: 1,
                          }}
                        />
                        
                        {/* Content */}
                        <Box sx={{ 
                          p: 2, 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flex: 1,
                        }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000", 
                              fontWeight: "bold", 
                              mb: 1 
                            }}
                          >
                            Completed Projects:
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{ 
                              fontWeight: "bold", 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000" 
                            }}
                          >
                            {projectStatusData.completed}
                          </Typography>
                        </Box>
                      </Card>

                      {/* Failed Projects Card */}
                      <Card
                        elevation={2}
                        sx={{
                          p: 0,
                          flex: 1,
                          display: "flex",
                          flexDirection: "row",
                          borderRadius: 2,
                          backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
                          overflow: 'hidden',
                        }}
                      >
                        {/* Left Gap/Spacing */}
                        <Box sx={{ width: '12px' }} />
                        
                        {/* Blue Vertical Line */}
                        <Box
                          sx={{
                            width: '4px',
                            backgroundColor: '#e73535',
                            borderRadius: '8px',
                            flexShrink: 0,
                            my: 1,
                          }}
                        />
                        
                        {/* Content */}
                        <Box sx={{ 
                          p: 2, 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flex: 1,
                        }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000", 
                              fontWeight: "bold", 
                              mb: 1 
                            }}
                          >
                            Failed Projects:
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{ 
                              fontWeight: "bold", 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000" 
                            }}
                          >
                            {projectStatusData.failed}
                          </Typography>
                        </Box>
                      </Card>

                      {/* In Progress Card */}
                      <Card
                        elevation={2}
                        sx={{
                          p: 0,
                          flex: 1,
                          display: "flex",
                          flexDirection: "row",
                          borderRadius: 2,
                          backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#ffffff",
                          overflow: 'hidden',
                        }}
                      >
                        {/* Left Gap/Spacing */}
                        <Box sx={{ width: '12px' }} />
                        
                        {/* Blue Vertical Line */}
                        <Box
                          sx={{
                            width: '4px',
                            backgroundColor: '#f7903c',
                            borderRadius: '8px',
                            flexShrink: 0,
                            my: 1,
                          }}
                        />
                        
                        {/* Content */}
                        <Box sx={{ 
                          p: 2, 
                          display: 'flex', 
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flex: 1,
                        }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000", 
                              fontWeight: "bold", 
                              mb: 1 
                            }}
                          >
                            In Progress:
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{ 
                              fontWeight: "bold", 
                              color: theme.palette.mode === "dark" ? "#ffffff" : "#000000" 
                            }}
                          >
                            {projectStatusData.inProgress}
                          </Typography>
                        </Box>
                      </Card>
                    </>
                  )}
                </Box>
              </Widget>
            </div>
          )}

          {selectedWidgets.issuesSeverity && (
            <div key="issuesSeverity">
              <Widget
                title="Issues by Severity"
                actions={
                  <>
                    <Select
                      value={selectedGranularity}
                      onChange={(e) => setSelectedGranularity(e.target.value)}
                      size="small"
                      sx={{ width: "100px" }}
                    >
                      <MenuItem value="day">Day</MenuItem>
                      <MenuItem value="month">Month</MenuItem>
                      <MenuItem value="year">Year</MenuItem>
                    </Select>
                    <Select
                      value={selectedCumulation}
                      onChange={(e) => setSelectedCumulation(e.target.value)}
                      size="small"
                      sx={{ width: "140px" }}
                    >
                      <MenuItem value="sum">Sum</MenuItem>
                      <MenuItem value="average">Average</MenuItem>
                    </Select>
                  </>
                }
              >
                <Box sx={{ 
                height: '100%',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                p: 2
              }}>
                  {loadingSeverity ? (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Skeleton variant="rectangular" height={250} sx={{ mb: 2, borderRadius: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        Loading severity data...
                      </Typography>
                    </Box>
                  ) : error ? (
                    <Alert severity="error">{error}</Alert>
                  ) : severityData ? (
                    <Box sx={{ 
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      minHeight: '250px'
                    }}>
                      <Bar
                        data={severityData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: "top",
                              labels: {
                                color: theme.palette.mode === "dark" ? "#fff" : "gray",
                              },
                            },
                          },
                          scales: {
                            x: {
                              ticks: {
                                color: theme.palette.mode === "dark" ? "#fff" : "gray",
                              },
                            },
                            y: {
                              beginAtZero: true,
                              ticks: {
                                color: theme.palette.mode === "dark" ? "#fff" : "gray",
                              },
                              title: {
                                display: true,
                                text: "Severity Count",
                                color: theme.palette.mode === "dark" ? "#fff" : "gray",
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                      <Typography color="textSecondary">No data available</Typography>
                    </Box>
                  )}
                </Box>
              </Widget>
            </div>
          )}

          {selectedWidgets.projectStatus && (
            <div key="projectStatus">
              <Widget title="Project Status Distribution">
                {loadingProjectStatus ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100%',
                    width: '100%'
                  }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ 
                    height: '100%',
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2
                  }}>
                    <Box sx={{ 
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      maxWidth: '400px',
                      maxHeight: '400px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <Pie
                        data={pieChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                              labels: {
                                color: theme.palette.mode === "dark" ? "#fff" : "gray",
                                padding: 12,
                                font: {
                                  size: 12,
                                },
                                boxWidth: 15,
                                boxHeight: 15,
                              },
                            },
                            tooltip: {
                              enabled: true,
                              bodyColor: theme.palette.mode === "dark" ? "#fff" : "#000",
                              titleColor: theme.palette.mode === "dark" ? "#fff" : "#000",
                              backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#fff",
                              borderColor: theme.palette.mode === "dark" ? "#666" : "#ddd",
                              borderWidth: 1,
                              padding: 12,
                              bodyFont: {
                                size: 13,
                              },
                              titleFont: {
                                size: 14,
                                weight: 'bold',
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Widget>
            </div>
          )}
        </ResponsiveGridLayout>
        </Box>

        <MaintenancePopup open={isPageInMaintenance('dashboard')} />
        
      </Box>
    </Layout>
  );
};

export default Dashboard;